import type { Side } from '../shared/espn'
import type { Team } from '../shared/teams'
import type { Ruleset } from '../shared/scoring'

/*
 * The client's whole view of the server.
 *
 * Note what is NOT here: any call to ESPN. The board is frozen on the server
 * at publish, so every phone sees identical teams, identical kickoffs and
 * identical spreads. A client that fetched live would reintroduce the problem
 * the freeze exists to solve - and would quietly break the underdog bonus,
 * which has to settle against the number everyone actually picked against.
 */

export interface StateGame {
  id: string
  slot: number
  league: 'nfl' | 'college-football'
  kickoffAt: number
  /*
   * `abbr` is ESPN's own abbreviation - "CSU", "DET" - and the only one of the
   * three names narrow enough for a grid cell. Null on every board published
   * before migration 004, so read it as `abbr ?? name`.
   */
  home: { name: string; full: string; abbr: string | null; logo: string | null; rank: number | null }
  away: { name: string; full: string; abbr: string | null; logo: string | null; rank: number | null }
  spread: number | null
  favSide: Side | null
  overUnder: number | null
  networks: string[]
  detail: string | null
  reason: 'pin' | 'gameday' | 'fill'
  /**
   * The BOARD's deadline has passed, so every pick on it is public.
   *
   * True for all ten games from the first kickoff onward. It is a statement
   * about visibility and nothing else - ask `started` about the game itself.
   */
  locked: boolean
  /** THIS game has kicked off. */
  started: boolean
  /*
   * How many people have picked this game. A count, never a side - which is
   * why it is safe before kickoff, when `picks` carries only your own. The
   * client cannot derive it: that is the whole point of the filter.
   */
  pickCount: number
  /*
   * The score, and - while the game is actually running - where it is.
   *
   * `period` and `clock` are the live badge's, and both are null far more
   * often than not: null before kickoff, null once the badge says FINAL, and
   * the clock null through every break, because what ESPN sends through a
   * stoppage is the last value it had rather than a running one. Null on every
   * row written before migration 005 as well. Read them as optional; a game
   * with neither renders the badge it always rendered.
   */
  result: {
    winnerSide: string | null
    final: boolean
    homeScore: number | null
    awayScore: number | null
    period: number | null
    clock: string | null
  } | null
}

export interface StatePlayer {
  id: string
  name: string
  /*
   * Who wears the chip. NOT an authority: admin routes are gated by
   * SETUP_PIN on the server, because a column the app reads is a fact about a
   * person and not a credential.
   */
  role: 'player' | 'commissioner'
  has_pin: number
  favorite_cfb?: string | null
  favorite_nfl?: string | null
}

/** A game as the draft screen sees it, before anyone can pick on it. */
export interface DraftGame extends Omit<StateGame, 'locked' | 'started' | 'result' | 'pickCount'> {
  lineSource: 'espn' | 'manual'
  /** Lets a screen match a board row to a search result by fixture. */
  espnId: string
}

export interface SeasonWeek {
  id: string
  number: number
  label: string
  games: number
  finals: number
}

export interface SeasonRow extends StandingsRow {
  correctByWeek: Record<string, number>
  bestWeekId: string | null
}

/** One search result on Add Game, with the family members who starred a side. */
export interface SearchGame {
  espnId: string
  league: 'nfl' | 'college-football'
  kickoffAt: number
  home: { name: string; full: string; abbr: string | null; logo: string | null; rank: number | null }
  away: { name: string; full: string; abbr: string | null; logo: string | null; rank: number | null }
  spread: number | null
  favSide: Side | null
  networks: string[]
  detail: string | null
  onBoard: boolean
  fans: string[]
}
export interface PlayerActivity {
  id: string
  name: string
  lastSeenAt: number | null
  logins: number
  lastLoginAt: number | null
  /** Approximate: occupied two-minute windows, not measured sessions. */
  minutesWeek: number
  minutesAll: number
  picks: number
  lastPickAt: number | null
  /** Picks somebody else entered for them - the shared-phone case. */
  picksByOthers: number
  devices: number
}

export interface StateCount { playerId: string; picked: number; lockedAt: number | null }

/** A stored favourite beside the team it resolves to, if any. See `me.resolvedFavorites`. */
export interface ResolvedFavorites {
  cfb: { stored: string | null; team: Team | null }
  nfl: { stored: string | null; team: Team | null }
}

export interface State {
  empty?: boolean
  me: { playerId: string | null; isAdmin: boolean }
  season?: { id: string; year: number; label: string }
  players: StatePlayer[]
  weeks: { id: string; number: number; label: string; state: string }[]
  week: {
    id: string; number: number; label: string; opensAt: number; closesAt: number; state: string
    /** The board's single deadline: its first kickoff. Null before a board exists. */
    lockAt: number | null
    /** True once that deadline has passed, when every pick becomes public. */
    locked: boolean
  } | null
  games: StateGame[]
  /** Already filtered by the server. Anything absent is not ours to see yet. */
  picks: { gameId: string; playerId: string; side: Side }[]
  counts: StateCount[]
  /** People asking to join, awaiting the commissioner. Always 0 for players. */
  pendingPlayers: number
  /** The family code. Sent to the commissioner only; null for everyone else. */
  joinCode: string | null
  rules: Ruleset
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`/api/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  })
  const body = await r.json().catch(() => ({}))
  if (!r.ok) throw new ApiError(r.status, (body as { error?: string }).error ?? 'Something went wrong.')
  return body as T
}

const post = <T>(path: string, data: unknown) =>
  call<T>(path, { method: 'POST', body: JSON.stringify(data) })

export const api = {
  join: (code: string) => post<{ ok: true }>('join', { code }),
  select: (playerId: string, pin?: string) =>
    post<{ ok: true; playerId: string; name: string; admin?: boolean }>('select', { playerId, pin }),
  elevate: (pin: string) => post<{ ok: true }>('elevate', { pin }),
  state: (week?: string) => call<State>(`state${week ? `?week=${week}` : ''}`),
  pick: (gameId: string, playerId: string, side: Side) =>
    post<{ ok: true }>('pick', { gameId, playerId, side }),
  lock: (weekId: string, playerId: string, locked: boolean) =>
    post<{ ok: true }>('lock', { weekId, playerId, locked }),
  standings: () => call<{ rows: StandingsRow[]; rules: Ruleset }>('standings'),
  season: () => call<{ weeks: SeasonWeek[]; rows: SeasonRow[] }>('season'),
  joinRoster: (name: string) =>
    post<{ ok: true; status: 'pending'; already?: boolean }>('join-roster', { name }),
  /*
   * The team picker's list, still through the server.
   *
   * Same rule as the board: the client does not talk to ESPN. Here it also
   * cannot - `cdn.espn.com` sends no CORS header a browser would accept - so
   * the Function proxies, and caches, the one roster query per league.
   */
  teams: (league: 'cfb' | 'nfl', q: string) =>
    call<{ teams: Team[] }>(`teams?league=${league}&q=${encodeURIComponent(q)}`),
  me: {
    pin: (pin: string | null) => post<{ ok: true; hasPin: boolean }>('me/pin', { pin }),
    name: (name: string) => post<{ ok: true; name: string }>('me/name', { name }),
    favorites: (f: { cfb?: string | null; nfl?: string | null }) =>
      post<{ ok: true }>('me/favorites', f),
    /*
     * What the stored favourites RESOLVE to, which is not the same question as
     * what they say. `team` is null for a value ESPN does not know, and the
     * profile has to show that as unset - a stored name the board can never
     * match is not a favourite, however much it looks like one.
     */
    resolvedFavorites: () => call<ResolvedFavorites>('me/favorites'),
  },
  admin: {
    candidates: (opensAt: number, closesAt: number, gamedayEspnId?: string | null) =>
      post<{ board: unknown[]; considered: number }>('admin/candidates', { opensAt, closesAt, gamedayEspnId }),
    publish: (w: { number: number; label: string; opensAt: number; closesAt: number; gamedayEspnId?: string | null }) =>
      post<{ ok: true; weekId: string; games: number }>('admin/publish', w),
    refresh: (weekId: string) => post<{ ok: true; updated: number }>('admin/refresh', { weekId }),
    rules: (r: Partial<Ruleset>) => post<{ ok: true; rules: Ruleset }>('admin/rules', r),
    pin: (playerId: string, pin: string | null) => post<{ ok: true }>('admin/pin', { playerId, pin }),
    draft: {
      get: () => call<{ week: DraftWeek | null; games: DraftGame[] }>('admin/draft'),
      create: (w: { number: number; label: string; opensAt: number; closesAt: number }) =>
        post<{ ok: true; weekId: string }>('admin/draft/create', w),
      add: (weekId: string, espnId: string) =>
        post<{ ok: true; slot: number }>('admin/draft/add', { weekId, espnId }),
      remove: (gameId: string) => post<{ ok: true }>('admin/draft/remove', { gameId }),
      line: (gameId: string, spread: number | null, favSide: Side | null) =>
        post<{ ok: true }>('admin/draft/line', { gameId, spread, favSide }),
      publish: (weekId: string) => post<{ ok: true }>('admin/draft/publish', { weekId }),
      suggest: (weekId: string) =>
        post<{ ok: true; added: number }>('admin/draft/suggest', { weekId }),
    },
    search: (weekId: string, q: string, filter: string) =>
      post<{ family: SearchGame[]; rest: SearchGame[]; counts?: Record<string, number> }>(
        'admin/search', { weekId, q, filter },
      ),
    player: (action: 'add' | 'rename' | 'remove', v: { playerId?: string; name?: string }) =>
      post<{ ok: true; id?: string }>('admin/player', { action, ...v }),
    autoDraft: (on: boolean) => post<{ ok: true; on: boolean }>('admin/autodraft', { on }),
    pending: () => call<{ pending: { id: string; name: string; created_at: number }[] }>('admin/pending'),
    activity: () => call<{ now: number; players: PlayerActivity[] }>('admin/activity'),
    decide: (playerId: string, approve: boolean) =>
      post<{ ok: true }>('admin/pending', { playerId, approve }),
  },
}

export interface DraftWeek {
  id: string
  number: number
  label: string
  opens_at: number
  closes_at: number
  state: 'draft' | 'published'
  board_size: number
}

export interface StandingsRow {
  playerId: string
  name: string
  total: number
  weeks: number
  perWeek: number
  byWeek: Record<string, number>
}
